import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { formatCurrencyAmount, formatNumberValue, getFormattedDate, getNumberOfStringCharacters } from "../utils/miscFunctions";
import { ITableAction } from "../components/elements/GTable/GTable";
import { ITransaction } from "../resources/interfaces";
import { AlertCircle, ArrowDown, ArrowUp, CheckCircle2, Clock, CreditCard, DollarSign, EllipsisVerticalIcon, Receipt } from "lucide-react";
import PopOnMe from "../components/elements/poponme/PopOnMe";
import { TRANSACTION_STATUS, TRANSACTION_TYPE } from "../resources/enums";


interface IUseTransactions {
    tableRowActions?: (row: ITransaction) => ITableAction[];
}

function useTransactions({ tableRowActions }: IUseTransactions) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();


    // Transaction status badge component
    const StatusBadge = ({ status }: { status: TRANSACTION_STATUS | string }) => {
        const getStatusInfo = () => {
            switch (status) {
                case TRANSACTION_STATUS.PENDING:
                    return { color: 'bg-yellow-100 text-yellow-800', icon: Clock };
                case TRANSACTION_STATUS.COMPLETED:
                    return { color: 'bg-green-100 text-green-800', icon: CheckCircle2 };
                case TRANSACTION_STATUS.FAILED:
                    return { color: 'bg-red-100 text-red-800', icon: AlertCircle };
                default:
                    return { color: 'bg-gray-100 text-gray-800', icon: Clock };
            }
        };

        const { color, icon: Icon } = getStatusInfo();

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${color}`}>
                <Icon className="w-3 h-3 mr-1" />
                <span className="capitalize">{status}</span>
            </span>
        );
    };

    // Transaction type badge component
    const TypeBadge = ({ type }: { type: TRANSACTION_TYPE | string }) => {
        const getTypeInfo = () => {
            // Default values
            let color = 'bg-gray-100 text-gray-800';
            let icon = DollarSign;
            let displayText = type?.toLowerCase().replace(/_/g, ' ');

            switch (type) {
                // Disbursements (outgoing payments)
                case TRANSACTION_TYPE.DISBURSEMENT:
                case TRANSACTION_TYPE.LOAN_DISBURSEMENT:
                    color = 'bg-blue-100 text-blue-800';
                    icon = ArrowDown;
                    break;
                
                // Repayments and deposits (incoming funds)
                case TRANSACTION_TYPE.LOAN_REPAYMENT:
                case TRANSACTION_TYPE.DEPOSIT:
                    color = 'bg-green-100 text-green-800';
                    icon = ArrowUp;
                    break;
                
                // Payments (outgoing)
                case TRANSACTION_TYPE.CLAIM_PAYMENT:
                case TRANSACTION_TYPE.COMMISSION_PAYMENT:
                case TRANSACTION_TYPE.TAX_PAYMENT:
                case TRANSACTION_TYPE.PREMIUM_PAYMENT:
                    color = 'bg-purple-100 text-purple-800';
                    icon = ArrowDown;
                    break;
                
                // Refunds (incoming)
                case TRANSACTION_TYPE.COMMISSION_REFUND:
                case TRANSACTION_TYPE.PREMIUM_REFUND:
                case TRANSACTION_TYPE.POLICY_CANCELLATION_REFUND:
                    color = 'bg-teal-100 text-teal-800';
                    icon = ArrowUp;
                    break;
                
                // Fees and charges
                case TRANSACTION_TYPE.SERVICE_FEE:
                case TRANSACTION_TYPE.PLATFORM_FEE:
                    color = 'bg-orange-100 text-orange-800';
                    icon = Receipt;
                    break;
                
                // Adjustments and other
                case TRANSACTION_TYPE.ADJUSTMENT:
                    color = 'bg-yellow-100 text-yellow-800';
                    icon = DollarSign;
                    break;
                
                case TRANSACTION_TYPE.WITHDRAWAL:
                    color = 'bg-red-100 text-red-800';
                    icon = ArrowDown;
                    break;
                
                // Default for any other types
                case TRANSACTION_TYPE.OTHER:
                default:
                    color = 'bg-gray-100 text-gray-800';
                    icon = CreditCard;
            }

            return { color, icon, displayText };
        };

        const { color, icon: Icon, displayText } = getTypeInfo();

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
                <Icon className="w-3 h-3 mr-1" />
                <span className="capitalize">{displayText}</span>
            </span>
        );
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Transactions',
                columns: [
                    // {
                    //     Header: 'Reference',
                    //     accessor: 'reference',
                    //     Cell: (row: any) => (
                    //         <span className="">
                    //             {row.cell.value || '-'}
                    //         </span>
                    //     )
                    // },
                    {
                        Header: 'Type',
                        accessor: 'type',
                        Cell: (row: any) => (
                            <span className="">
                                {row.cell.value ? row.cell.value : '-'}
                            </span>
                        )
                    },
                    {
                        Header: 'Description',
                        accessor: 'description',
                        Cell: (row: any) => (
                            <span className="">
                                {getNumberOfStringCharacters(row.cell.value, 0, 40) || '-'}
                            </span>
                        )
                    },
                    {
                        Header: 'Amount',
                        accessor: 'amount',
                        Cell: (row: any) => (
                            <span className="">
                                {formatCurrencyAmount(row.cell.value)}
                            </span>
                        )
                    },
                    {
                        Header: 'From',
                        accessor: 'fromCompanyName',
                        Cell: (row: any) => (
                            <span className="">
                                {row.cell.value || '-'}
                            </span>
                        )
                    },
                    {
                        Header: 'To',
                        accessor: 'toCompanyName',
                        Cell: (row: any) => (
                            <span className="">
                                {row.cell.value || '-'}
                            </span>
                        )
                    },
                    {
                        Header: 'Date',
                        accessor: 'transactionDate',
                        Cell: (row: any) => (
                            <span className="">
                                {getFormattedDate({ date: row.cell.value })}
                            </span>
                        )
                    }, {
                        Header: 'Status',
                        accessor: 'status',
                        Cell: (row: any) => <StatusBadge status={row.cell.value} />
                    },
                    ...(tableRowActions ? [{
                        id: 'action',
                        Cell: (row: any) => (
                            <div className="cursor-pointer flex justify-center">
                                <PopOnMe actions={tableRowActions?.(row.row.original) ?? []}>
                                    <EllipsisVerticalIcon className="h-5 w-5 text-gray-500 hover:text-gray-700" />
                                </PopOnMe>
                            </div>
                        )
                    }] : [])
                ]
            }
        ],
        []
    );



    return {
        columns,
        TypeBadge,
        StatusBadge
    }
}

export default useTransactions;
